/**
 * Componente de bolha de digitação animada
 * Exibe indicador visual de "contato digitando" ou "gravando áudio" na área de mensagens
 * Estilo similar à bolha de mensagem recebida (lado esquerdo)
 * 
 * Animações:
 * - Digitando: 3 pontos com bounce sequencial
 * - Gravando: Ponto vermelho pulsante com texto
 * 
 * @author UaizyChat
 * @version 1.0.0
 */

import { cn } from '@/lib/utils';

interface TypingBubbleProps {
  /** Indica se o contato está digitando */
  isTyping: boolean;
  /** Indica se o contato está gravando áudio */
  isRecording: boolean;
  /** Nome do contato (opcional, para exibição futura) */
  contactName?: string;
}

/**
 * Bolha de digitação/gravação para exibir na área de mensagens
 * Posicionada à esquerda, similar às mensagens recebidas
 */
export function TypingBubble({ isTyping, isRecording, contactName }: TypingBubbleProps) {
  // Se não está digitando nem gravando, não renderiza
  if (!isTyping && !isRecording) return null;

  return (
    <div 
      className={cn(
        // Container similar às mensagens recebidas (lado esquerdo)
        "flex items-end gap-2 justify-start px-2 md:px-4 pb-3",
        // Animação de entrada suave
        "animate-in fade-in slide-in-from-bottom-2 duration-300"
      )}
    >
      <div className="max-w-[85%] md:max-w-[65%] flex items-end gap-1">
        {/* Bolha com estilo de mensagem recebida */}
        <div 
          className={cn(
            // Estilo de bolha de mensagem recebida
            "bg-muted rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm",
            // Altura mínima para manter consistência visual
            "min-h-[40px] flex items-center justify-center"
          )}
        >
          {isRecording ? (
            // ========== INDICADOR DE GRAVAÇÃO ==========
            // Ponto vermelho pulsante + texto "Gravando áudio..."
            <div className="flex items-center gap-2">
              <div 
                className={cn(
                  "w-2 h-2 rounded-full",
                  // Cor vermelha para indicar gravação
                  "bg-destructive",
                  // Animação de pulso
                  "animate-pulse"
                )} 
              />
              <span className="text-xs text-muted-foreground">
                Gravando áudio...
              </span>
            </div>
          ) : (
            // ========== INDICADOR DE DIGITAÇÃO ==========
            // 3 pontos com animação de bounce sequencial
            <div className="flex items-center gap-1">
              <span 
                className={cn(
                  "w-2 h-2 rounded-full bg-muted-foreground/60",
                  "animate-bounce"
                )}
                style={{ animationDelay: '0ms' }}
              />
              <span 
                className={cn(
                  "w-2 h-2 rounded-full bg-muted-foreground/60",
                  "animate-bounce"
                )}
                style={{ animationDelay: '150ms' }}
              />
              <span 
                className={cn(
                  "w-2 h-2 rounded-full bg-muted-foreground/60",
                  "animate-bounce"
                )}
                style={{ animationDelay: '300ms' }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
